import React from 'react';
import { domToReact, attributesToProps } from 'html-react-parser';
import { ACTION_SEQUENCE_STEP_TYPE } from '../../hooks/actions/steptypes';
import useConditionalDisplay from '../../hooks/display';
import DISPLAY_ACTION from '../../hooks/display/types';
import { lookupComponent } from '../../components/PageDesigner/Library/Components';
import { consoleLog } from '../../utils/Logger';

const Tag = ({
  renderComponentMarkup,
  domNode,
  tagName,
  children,
  element,
  designMode,
  page,
  data,
  ...props
}) => {
  const { conditionalDisplayResult } = useConditionalDisplay({
    element,
    page,
    designMode
  });

  if (!designMode) {
    if (conditionalDisplayResult.action === DISPLAY_ACTION.HIDE_ELEMENT) {
      return null;
    }

    if (
      conditionalDisplayResult.action === DISPLAY_ACTION.SHOW_ELEMENT ||
      conditionalDisplayResult.action ===
        DISPLAY_ACTION.MODIFY_ATTRIBUTES_OR_CLASSES
    ) {
      if (designMode) {
        return false;
      }

      const {
        //htmlElementId,
        addClasses,
        removeClasses,
        addAttributes,
        removeAttributes
      } = conditionalDisplayResult;

      let classesToAdd =
        addClasses?.trim().length > 0 ? addClasses.split(' ') : [];
      const classesToRemove =
        removeClasses?.trim().length > 0 ? removeClasses.split(' ') : [];

      let attrsToAdd =
        addAttributes?.trim().length > 0 ? addAttributes.split(' ') : [];
      const attrsToRemove =
        removeAttributes?.trim().length > 0 ? removeAttributes.split(' ') : [];

      let existingClasses =
        props.className?.trim().length > 0
          ? props.className.trim().split(' ')
          : [];
      existingClasses = existingClasses.filter(
        c => !classesToRemove.some(o => o === c)
      );
      classesToAdd = classesToAdd.filter(
        c => !existingClasses.some(o => o === c)
      );

      existingClasses = [...existingClasses, ...classesToAdd];
      props.className = existingClasses.join(' ');

      attrsToRemove.forEach(attr => {
        const parts = attr.split('=');
        if (parts.length === 2 && props[parts[0]]) {
          delete props[parts[0]];
        }
      });

      attrsToAdd.forEach(attr => {
        const parts = attr.split('=');
        if (parts.length === 2 && props[parts[0]] === undefined) {
          props[parts[0]] = parts[1];
        }
      });
    }
  }

  const component = lookupComponent({
    domNode,
    designMode,
    page,
    props,
    data,
    metadata: element,
    children,
    renderComponentMarkup
  });
  if (!component) {
    return React.createElement(tagName, props, children);
  }
  return component;
};

const getParserOptions = ({
  page,
  data = {},
  designMode = false,
  renderComponentMarkup = false
}) => {
  return {
    replace: domNode => {
      let children;
      if (domNode?.children) {
        /*children = domToReact(
          domNode.children,
          getParserOptions({ page, data, designMode })
        );*/
      }
      if (!domNode.attribs) {
        if (domNode?.name) {
          return lookupComponent({
            domNode,
            designMode,
            props: {},
            page,
            data,
            children,
            renderComponentMarkup
          });
        }
        return;
      }

      const metadata = page?.metadata
        ? page?.metadata[domNode.attribs.id] || {}
        : {};
      const eventProps = {};

      const validateElement = async e => {
        e.preventDefault();

        if (designMode) {
          return false;
        }

        const results = await window.actionSequencer.run({
          step: {
            type: ACTION_SEQUENCE_STEP_TYPE.VALIDATE_ELEMENT,
            validateFormElementId: domNode.attribs.id,
            validateFormId: e.target.form?.id
          },
          data,
          page
        });
        consoleLog('actionSequencer.results', results);
        let classesToAdd =
          results && results?.validations[domNode.attribs.id]?.error
            ? metadata.errorClasses || ''
            : metadata.validatedClasses || '';
        classesToAdd
          .split(' ')
          .filter(className => className?.trim().length > 0)
          .forEach(className => e.target.classList.add(className));

        let classesToRemove = `${metadata.removeClasses} ${
          results && results?.validations[domNode.attribs.id]?.error
            ? metadata.validatedClasses || ''
            : metadata.errorClasses || ''
        }`
          .trim()
          ?.split(' ');

        if (Array.isArray(classesToAdd)) {
          classesToRemove = classesToRemove.filter(
            c => !classesToAdd.some(a => a.trim() === c.trim())
          );
        }

        if (Array.isArray(classesToRemove) && classesToRemove.length > 0) {
          classesToRemove.forEach(className =>
            e.target.classList.remove(className)
          );
        }
      };

      const executeElementAction = async e => {
        if (designMode) {
          return false;
        }

        if (!['checkbox', 'radio'].some(i => i === e.target.type)) {
          e.preventDefault();
        }
        // eslint-disable-next-line
        if (metadata.actionSequenceType === 'execute') {
          window.actionSequencer.execute({
            value: e?.target?.value,
            actionSequenceId: metadata.actionSequenceId,
            page,
            data,
            selectedDocumentId: domNode?.attribs
              ? domNode?.attribs['data-documentId'.toLowerCase()]
              : undefined
          });
        } else {
          let payload = {};
          switch (metadata.actionSequenceType) {
            case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_BACK: {
              payload = {
                type: metadata.actionSequenceType
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_PAGE: {
              payload = {
                type: metadata.actionSequenceType,
                navigationPagePath: metadata.navigationPagePath
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_ROUTE: {
              payload = {
                type: metadata.actionSequenceType,
                navigationRoute: metadata.navigationRoute
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_URL: {
              payload = {
                type: metadata.actionSequenceType,
                navigationUrl: metadata.navigationUrl,
                navigationUrlTarget: metadata.navigationUrlTarget
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.ELEMENT_HIDE: {
              payload = {
                type: metadata.actionSequenceType,
                elementId: metadata.targetElementId
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.ELEMENT_SHOW: {
              payload = {
                type: metadata.actionSequenceType,
                elementId: metadata.targetElementId
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.ELEMENT_TOGGLE: {
              payload = {
                type: metadata.actionSequenceType,
                elementId: metadata.targetElementId
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.LOCAL_STORE_VALUE_REMOVE: {
              payload = {
                type: metadata.actionSequenceType,
                localStoreKeyName: metadata.localStoreKeyName
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.LOCAL_STORE_VALUE_SET: {
              let value =
                metadata.localStoreKeyValue === '{value}'
                  ? e.target.value || e.target.getAttribute('value')
                  : metadata.localStoreKeyValue;

              let localStoreKeyValueIsJson = false;

              try {
                if (value.indexOf('{') > -1 && value !== '{value}') {
                  value = JSON.parse(value);
                  localStoreKeyValueIsJson = true;
                }
              } catch {
                //na
              }

              if (
                value === undefined ||
                value === null ||
                value === '{value}'
              ) {
                if (
                  domNode?.attribs &&
                  domNode?.attribs['data-documentId'.toLowerCase()] !==
                    undefined
                ) {
                  value = domNode?.attribs['data-documentId'.toLowerCase()];
                }
              }

              payload = {
                type: metadata.actionSequenceType,
                localStoreKeyName: metadata.localStoreKeyName,
                localStoreIgnoreInputData: true,
                localStoreKeyValue: value,
                localStoreKeyValueIsJson
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.LOCAL_STORE_VALUE_TOGGLE: {
              payload = {
                type: metadata.actionSequenceType,
                localStoreKeyName: metadata.localStoreKeyName,
                localStoreIgnoreInputData: true
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.EXECUTE_CODE: {
              payload = {
                type: metadata.actionSequenceType,
                executeCode: metadata.executeCode,
                computeLocalStoreKey: metadata.localStoreKeyName
              };
              break;
            }
            case ACTION_SEQUENCE_STEP_TYPE.USER_LOGOUT: {
              payload = {
                type: metadata.actionSequenceType
              };
              break;
            }

            default:
              break;
          }

          if (Object.keys(payload).length > 0) {
            window.actionSequencer.run({
              step: payload,
              page,
              data
            });
          }
        }
      };

      if (Object.keys(metadata).length > 0) {
        ['onClick', 'onBlur', 'onFocus', 'onChange'].forEach(eventName => {
          if (
            metadata?.validateOn?.toLowerCase() === eventName.toLowerCase() ||
            metadata.action?.toLowerCase() === eventName.toLowerCase()
          ) {
            eventProps[eventName] = async e => {
              if (
                metadata?.validateOn?.toLowerCase() === eventName.toLowerCase()
              ) {
                validateElement(e);
              }
              if (metadata.action?.toLowerCase() === eventName.toLowerCase()) {
                executeElementAction(e);
              }
            };
          }
        });

        if (['select'].some(e => e === domNode.name.toLowerCase())) {
          let value;
          if (
            data &&
            (metadata.databindingDisabled === false ||
              metadata.databindingDisabled === 'false')
          ) {
            if (data[metadata.dataLinkName] !== undefined) {
              value = data[metadata.dataLinkName][metadata.dataLinkField];
            }
          }

          try {
            if (value.toLowerCase) {
              if (value.toLowerCase().trim() === 'true') {
                value = 'true';
              } else if (value.toLowerCase().trim() === 'false') {
                value = 'false';
              }
            }
          } catch {
            //na
          }

          delete domNode.attribs.value;

          // do not remove the key={} property
          //  see https://stackoverflow.com/questions/37443993/reactjs-value-of-previous-input-is-set-by-default-in-new-input
          return (
            <Tag
              renderComponentMarkup={renderComponentMarkup}
              designMode={designMode}
              tagName={domNode.name}
              element={metadata}
              domNode={domNode}
              children
              page={page}
              data={data}
              key={`key_${domNode.attribs.id}`}
              {...attributesToProps(domNode.attribs)}
              defaultValue={value}
              {...eventProps}>
              {domToReact(
                domNode.children,
                getParserOptions({
                  page,
                  data,
                  designMode
                })
              )}
            </Tag>
          );
        }

        if (
          ['input', 'img', 'textarea'].some(
            e => e === domNode.name.toLowerCase()
          )
        ) {
          let value = '';
          if (
            data &&
            (metadata.databindingDisabled === false ||
              metadata.databindingDisabled === 'false' ||
              metadata.databindingDisabled === '')
          ) {
            if (
              data[metadata.dataLinkName] !== undefined &&
              data[metadata.dataLinkName][metadata.dataLinkField] !== undefined
            ) {
              value = data[metadata.dataLinkName][metadata.dataLinkField];
            }
          } else if (
            domNode.attribs.value !== undefined &&
            domNode.attribs.value !== 'undefined'
          ) {
            value = domNode.attribs.value;
          }

          delete domNode.attribs.value;

          // inputs, textareas and images don't have children
          // do not remove the key={} property
          //  see https://stackoverflow.com/questions/37443993/reactjs-value-of-previous-input-is-set-by-default-in-new-input
          return (
            <Tag
              domNode={domNode}
              designMode={designMode}
              tagName={domNode.name}
              element={metadata}
              key={`key_${domNode.attribs.id}`}
              page={page}
              data={data}
              {...attributesToProps(domNode.attribs)}
              defaultValue={value}
              {...eventProps}
            />
          );
        }

        return (
          <Tag
            renderComponentMarkup={renderComponentMarkup}
            domNode={domNode}
            designMode={designMode}
            tagName={domNode.name}
            element={metadata}
            children
            page={page}
            data={data}
            {...attributesToProps(domNode.attribs)}
            {...eventProps}>
            {domToReact(
              domNode.children,
              getParserOptions({ page, data, designMode })
            )}
          </Tag>
        );
      }

      return lookupComponent({
        domNode,
        designMode,
        metadata,
        page,
        data,
        props: attributesToProps(domNode.attribs),
        children,
        renderComponentMarkup
      });
    }
  };
};

export default getParserOptions;
